import {
    useState,
    useCallback,
    useEffect
} from 'react';
import TabPanel, { Item as TabPanelItem } from 'devextreme-react/tab-panel';
import DataGridSpecificCostEstimate from './DataGridSpecificCostEstimate';
import GetFetch from '../../hooks/GetFetch';
import { KeyboardNavigation } from 'devextreme-react/data-grid';
import FiscalYearSelectDropdown from '../FiscalYearSelectDropdown'
import { BaseToggle } from "../../components/BaseToggle/BaseToggle";
import './SpecificCostEstimate.scss';

export default function SpecificCostEstimate({feeTypeFilter, pageTitle}) {
    const [selectedFiscalYear, setSelectedFiscalYear] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(undefined);
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [specificPostsTabData, setSpecificPostsTabData] = useState({
        0: null, // Current Year == Fiscal Year
        1: null, // Budget Year == Fiscal Year +1 
        2: null, // Budget Year+1 == Fiscal Year +2
        3: null, // Budget Year+2 == Fiscal Year +3
        4: null  // Budget Year+3 == Fiscal Year +4
    });
    const [isTabDataReset, setIsDataReset] = useState(false);
    const [groupingToggle, setGroupingToggle] = useState(true);
    const [enableToggle, setEnableToggle] = useState(true);
    const [showDataGrid, setShowDataGrid] = useState(false);

    const fetchPostsByFiscalYear = useCallback(async (tabIndex) => {
        if (!selectedFiscalYear || specificPostsTabData[tabIndex]) return;
        setLoading(true);

        const { Success, Message, Errors } = await GetFetch(`/v1/Post/GetPostsByFiscalYear/${selectedFiscalYear + tabIndex}`);

        if (Success) {
            setEnableToggle(false);
            setSpecificPostsTabData(prevData => ({
                ...prevData,
                [tabIndex]: Message.filter(post => post?.FeeType?.FeeTypeName == feeTypeFilter)
            }));
        };

        if (Errors.length > 0) {
            setEnableToggle(true);
            setError("No records were found for this budget year. Please select another budget year.");
        };

        setShowDataGrid(true);
        setLoading(false);
        
    }, [selectedFiscalYear, specificPostsTabData]);

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSubmit();
            setIsDataReset(true);
        };
    };

    const handleSubmit = async (param) => {
        setSpecificPostsTabData({ 0: null, 1: null, 2: null, 3: null, 4: null });
        setIsDataReset(true);
        setSelectedFiscalYear(param)
    };

    useEffect(()=> {
        if (isTabDataReset) {
            fetchPostsByFiscalYear(0);
            setIsDataReset(false);
        }
    }, [isTabDataReset]);

    const onTabSelectionChanged = useCallback((e) => {
        const newTabIndex = e.component.option('selectedIndex');
        setSelectedTabIndex(newTabIndex);
        fetchPostsByFiscalYear(newTabIndex);
    }, [fetchPostsByFiscalYear]);


    return (
        <div>
            <h1 style={{ color: "#06152B" }}>{pageTitle}</h1>
            <em style={{ color: "#06152B" }}>
                Please select a current year
            </em>
            <div style={{
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "flex-start",
                    width: '100%'
                }}
            >
                <FiscalYearSelectDropdown
                    enableSearch={true}
                    inputAttribute={{ "aria-label": "Current Year *" }}
                    isVisible={true}
                    label={"Current Year"}
                    onEnterKeyDown={handleKeyDown}
                    optionUpdatedByUser={({ selectedItem }) => handleSubmit(selectedItem)}
                    value={selectedFiscalYear}
                />
                <BaseToggle
                    disabled={enableToggle}
                    elementAttribute={{ id: 'switch-element-attribute' }}
                    hint={groupingToggle ? "Click to enable grouping" : "Click to disable grouping"}
                    name={"toggle grouping"}
                    rightToLeftEnabled={true}
                    switchedOffText={"Disable Grouping"}
                    switchedOnText={"Enable Grouping"}
                    value={groupingToggle} 
                    valueUpdatedByUser={({value}) => setGroupingToggle(value)}
                    width={120}
                />
            </div>
            <div className='inline_div--tabPanel-wrapper'>
                <TabPanel 
                    disabled={selectedFiscalYear === '' ? true : false}
                    id="tabPanel" 
                    onSelectionChanged={onTabSelectionChanged} 
                    selectedIndex={selectedTabIndex} 
                >
                    <KeyboardNavigation enabled={true} />
                    <TabPanelItem 
                        aria-label="cy-cost-est"
                        id="cy-cost-est" 
                        title={selectedFiscalYear === '' ? 'Current Year' : `Current Year (${selectedFiscalYear})`} 
                    >
                        <DataGridSpecificCostEstimate 
                            error={error}
                            fiscalYear={selectedFiscalYear} 
                            groupingEnabled={groupingToggle}
                            isLoading={loading}
                            posts={specificPostsTabData[0]} 
                            showDataGrid={showDataGrid}
                        />
                    </TabPanelItem>
                    <TabPanelItem 
                        aria-label="budget-year-cost-est"
                        id="budget-year-cost-est" 
                        title={selectedFiscalYear === '' ? 'Budget Year + 1' :  `Budget Year (${selectedFiscalYear + 1})`} 
                    >
                        <DataGridSpecificCostEstimate 
                            error={error}
                            fiscalYear={selectedFiscalYear + 1} 
                            groupingEnabled={groupingToggle}
                            isLoading={loading}
                            posts={specificPostsTabData[1]} 
                            showDataGrid={showDataGrid}
                        />
                    </TabPanelItem>
                    <TabPanelItem 
                        aria-label="budget-year-cost-est+1"
                        id="budget-year-cost-est+1" 
                        title={selectedFiscalYear === '' ? 'Budget Year + 2' : `Budget Year (${selectedFiscalYear + 2})`} 
                    >
                        <DataGridSpecificCostEstimate 
                            error={error}
                            fiscalYear={selectedFiscalYear + 2} 
                            groupingEnabled={groupingToggle} 
                            isLoading={loading}
                            posts={specificPostsTabData[2]} 
                            showDataGrid={showDataGrid}
                        />
                    </TabPanelItem>
                    <TabPanelItem 
                        aria-label="budget-year-cost-est+2"
                        id="budget-year-cost-est+2" 
                        title={selectedFiscalYear === '' ? 'Budget Year + 2' : `Budget Year (${selectedFiscalYear + 3})`} 
                    >
                        <DataGridSpecificCostEstimate 
                            error={error}
                            fiscalYear={selectedFiscalYear + 3} 
                            groupingEnabled={groupingToggle} 
                            isLoading={loading}
                            posts={specificPostsTabData[3]} 
                            showDataGrid={showDataGrid}
                        />
                    </TabPanelItem>
                    <TabPanelItem 
                        aria-label="budget-year-cost-est+4"
                        id="budget-year-cost-est+4" 
                        title={selectedFiscalYear === '' ? 'Budget Year + 3' : `Budget Year (${selectedFiscalYear + 4})`} 
                    >
                        <DataGridSpecificCostEstimate 
                            error={error}
                            fiscalYear={selectedFiscalYear + 4} 
                            groupingEnabled={groupingToggle} 
                            isLoading={loading}
                            posts={specificPostsTabData[4]} 
                            showDataGrid={showDataGrid}
                        />
                    </TabPanelItem>
                </TabPanel>
            </div>
        </div>
    );
}